import React, { useMemo, useRef, useState } from "react"
import ReportColumnSelector from "components/Common/ReportColumnSelector"
import { Button, Col, Form, Input, Label, Row } from "reactstrap"
import "flatpickr/dist/themes/material_blue.css"
import Flatpickr from "react-flatpickr"
import { useSelector, useDispatch } from "react-redux"
import { isObject } from "lodash"
import Select from "react-select"
import { getProviderItems } from "store/provider-dashboard/provider-dashboard-action"

const MEReportFilter = ({ onAdvancedSearchSubmit, loading, onCategoryChange }) => {
  const receiptDateFlatpickerfrom = useRef(null)
  const receiptDateFlatpickerto = useRef(null)
  const distributionDateFlatpickerfrom = useRef(null)
  const distributionDateFlatpickerto = useRef(null)
  const expireDateFlatpickerfrom = useRef(null)
  const expireDateFlatpickerto = useRef(null)

  const [project, setProject] = useState(null)
  const [province, setProvince] = useState(null)
  const [ip, setIp] = useState("")
  const [dsNumber, setDsNumber] = useState("")
  const [warehouse, setWarehouse] = useState(null)
  const [category, setCategory] = useState(null)
  const [item, setItem] = useState(null)
  const [facility, setFacility] = useState(null)
  const [hfType, setHfType] = useState(null)
  const [batchNumber, setBatchNumber] = useState("")
  const [quarter, setQuarter] = useState(null)
  const [year, setYear] = useState(null)
  
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)

  const dispatch = useDispatch()

  // Generate year options (from 2020 to current year)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = 2020; i <= currentYear; i++) {
      years.push({ value: i, label: i.toString() })
    }
    return years.reverse()
  }, [])

  // Check if date filters are active
  const isDateFilterActive = fromDate || toDate
  
  // Check if quarter/year filters are active
  const isQuarterYearFilterActive = quarter || year

  // Retrieve data from Redux store
  const projects = useSelector(state => state.providerDashboard.providerProjects)
  const provinces = useSelector(state => state.providerDashboard.providerProvinces)
  const warehouses = useSelector(state => state.providerDashboard.providerWarehouses)
  const categories = useSelector(state => state.providerDashboard.ProviderItemCategoires)
  const items = useSelector(state => state.providerDashboard.ProviderItems)
  const facilities = useSelector(state => state.providerDashboard.providerFacilities)

  // Get filtered items based on select box props needs
  const filteredItems = useMemo(
    () =>
      items?.data?.map(item => ({
        value: item.id,
        label: `${item.unfp_item_name} ( ${item.item_name} )`,
      })),
    [items]
  )

  // Store filtered data and dispatch the action
  const handleSubmit = () => {
    let data = {}

    data.project_id = isObject(project) ? project.value : null
    data.province_id = isObject(province) ? province.value : null
    data.ip_name = ip
    data.ds_number = dsNumber
    data.warehouse_id = isObject(warehouse) ? warehouse.value : null
    data.item_category_id = isObject(category) ? category.value : null
    data.item_id = isObject(item) ? item.value : null
    data.facility_id = isObject(facility) ? facility.value : null
    data.hf_type = isObject(hfType) ? hfType.value : null
    data.batch_number = batchNumber
    data.quarter = isObject(quarter) ? quarter.value : null
    data.year = isObject(year) ? year.value : null

    data.from_date = fromDate || null
    data.to_date = toDate || null

    onAdvancedSearchSubmit(data)
  }

  // Clear search when clear button clicked
  const onClearHandler = () => {
    receiptDateFlatpickerfrom.current?.flatpickr?.clear()
    receiptDateFlatpickerto.current?.flatpickr?.clear()

    setProject(null)
    setProvince(null)
    setIp("")
    setDsNumber("")
    setWarehouse(null)
    setCategory(null)
    setItem(null)
    setFacility(null)
    setHfType(null)
    setBatchNumber("")
    setQuarter(null)
    setYear(null)
    setFromDate(null)
    setToDate(null)
    
    onAdvancedSearchSubmit({})
  }

  // Store Item Categories and fetch items based on item category
  const onCategoryChangeHandler = cate => {
    setCategory(cate)
    if (cate && onCategoryChange) {
      onCategoryChange(cate.value)
    }
    dispatch(getProviderItems(cate?.value))
  }

  return (
    <Form
      onSubmit={e => {
        e.preventDefault()
        handleSubmit()
        return false
      }}
    >
      <div className="mb-3">
        <Row>
          <Col md={3}>
            <Label className="form-label">Project</Label>
            {projects?.data && (
              <Select
                options={projects.data}
                value={project}
                onChange={setProject}
                isClearable
                placeholder="Select Project"
              />
            )}
          </Col>
          <Col md={3}>
            <Label className="form-label">Province</Label>
            {provinces?.data && (
              <Select
                options={provinces.data}
                value={province}
                onChange={setProvince}
                isClearable
                placeholder="Select Province"
              />
            )}
          </Col>
          <Col md={3}>
            <Label className="form-label">Provider / IP</Label>
            <Input
              type="text"
              value={ip}
              onChange={e => setIp(e.target.value)}
              placeholder="Enter Provider/IP Name"
            />
          </Col>
          <Col md={3}>
            <Label className="form-label">Item Category</Label>
            {categories?.data && (
              <Select
                options={categories.data}
                value={category}
                onChange={onCategoryChangeHandler}
                isClearable
                placeholder="Select Item Category"
              />
            )}
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={3}>
            <Label className="form-label">Item</Label>
            {filteredItems && (
              <Select
                options={filteredItems}
                value={item}
                onChange={setItem}
                isClearable
                placeholder="Select Item"
              />
            )}
          </Col>
          <Col md={3}>
            <Label className="form-label">From Date</Label>
            <Flatpickr
              key={`from-date-${fromDate}`}
              ref={receiptDateFlatpickerfrom}
              className="form-control d-block"
              placeholder="From Date"
              value={fromDate ? new Date(fromDate) : null}
              disabled={isQuarterYearFilterActive}
              options={{
                dateFormat: "d-m-Y",
                allowInput: true,
                clickOpens: !isQuarterYearFilterActive,
              }}
              onChange={date => {
                const selectedDate = date[0]
                  ? new Date(date[0]).toLocaleDateString("en-CA")
                  : null
                setFromDate(selectedDate)
              }}
            />
          </Col>
          <Col md={3}>
            <Label className="form-label">To Date</Label>
            <Flatpickr
              key={`to-date-${toDate}`}
              ref={receiptDateFlatpickerto}
              className="form-control d-block"
              placeholder="To Date"
              value={toDate ? new Date(toDate) : null}
              disabled={isQuarterYearFilterActive}
              options={{
                dateFormat: "d-m-Y",
                allowInput: true,
                clickOpens: !isQuarterYearFilterActive,
              }}
              onChange={date => {
                const selectedDate = date[0]
                  ? new Date(date[0]).toLocaleDateString("en-CA")
                  : null
                setToDate(selectedDate)
              }}
            />
          </Col>
          <Col md={3}>
            <Label className="form-label">HF Type</Label>
            <Select
              options={[
                { value: "BHC", label: "BHC" },
                { value: "CHC", label: "CHC" },
                { value: "DH", label: "DH" },
                { value: "FHH", label: "FHH" },
                { value: "PHC", label: "PHC" },
                { value: "RH", label: "RH" },
                { value: "SHC", label: "SHC" },
              ]}
              value={hfType}
              onChange={setHfType}
              isClearable
              placeholder="Select HF Type"
            />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={3}>
            <Label className="form-label">HF Name / Facility</Label>
            {facilities?.data && (
              <Select
                options={facilities.data}
                value={facility}
                onChange={setFacility}
                isClearable
                placeholder="Select Facility"
              />
            )}
          </Col>
          <Col md={3}>
            <Label className="form-label">Batch Number</Label>
            <Input
              type="text"
              value={batchNumber}
              onChange={e => setBatchNumber(e.target.value)}
              placeholder="Enter Batch Number"
            />
          </Col>
          <Col md={6}>
            <Row>
              <Col md={6}>
                <Label className="form-label">Quarter</Label>
                <Select
                  options={[
                    { value: "Q1", label: "Q1 (Jan-Mar)" },
                    { value: "Q2", label: "Q2 (Apr-Jun)" },
                    { value: "Q3", label: "Q3 (Jul-Sep)" },
                    { value: "Q4", label: "Q4 (Oct-Dec)" },
                  ]}
                  value={quarter}
                  onChange={setQuarter}
                  isDisabled={isDateFilterActive}
                  isClearable
                  placeholder="Select Quarter"
                />
              </Col>
              <Col md={6}>
                <Label className="form-label">Year</Label>
                <Select
                  options={yearOptions}
                  value={year}
                  onChange={setYear}
                  isDisabled={isDateFilterActive}
                  isClearable
                  placeholder="Select Year"
                />
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={3}>
            <Label className="form-label">DS Number</Label>
            <Input
              type="text"
              value={dsNumber}
              onChange={e => setDsNumber(e.target.value)}
              placeholder="Enter DS Number"
            />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col>
            <Button
              type="submit"
              color="primary"
              className="btn btn-primary w-md me-2"
              disabled={loading}
            >
              Search
            </Button>
            <Button
              type="button"
              color="secondary"
              className="btn btn-secondary w-md"
              onClick={onClearHandler}
              disabled={loading}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </div>
    </Form>
  )
}

export default MEReportFilter
